import type { AsyncData } from 'nuxt/app';
import type { FetchOptions } from 'ofetch';
import type { ErrorResponse } from '@/types/externals/obbsApi/commons/Errors';

type Options = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  query?: FetchOptions['query'];
  headers?: Record<string, string>;
  params?: FetchOptions['params'];
  body?: FetchOptions['body'];
  key?: string;
  getCachedData?: (key: string) => any | undefined;
};

const CSRF_COOKIE = 'XSRF-TOKEN';
const CSRF_HEADER = 'X-XSRF-TOKEN';

// CSRFトークンの初期化
const initCsrf = async (apiUrl: string) => {
  await useFetch('/sanctum/csrf-cookie', {
    baseURL: apiUrl,
    credentials: 'include',
  });
};

const getCookie = (name: string): string | null => {
  if (process.server) {
    const cookies = useRequestHeaders(['cookie'])['cookie'];
    const match = cookies && cookies.match(new RegExp(`(^|;\\s*)(${name})=([^;]*)`));
    return match ? decodeURIComponent(match[3]) : null;
  } else {
    const match = document.cookie.match(new RegExp(`(^|;\\s*)(${name})=([^;]*)`));
    return match ? decodeURIComponent(match[3]) : null;
  }
};

/**
 * useApiFetch - APIリクエストを簡単に行うための関数
 *
 * この関数はAPIエンドポイントに対してHTTPリクエストを行います。
 * CSRFトークンの管理やヘッダーの設定もこの関数内で自動的に行われます。
 *
 * @template T - APIレスポンスの型
 * @param {string} url - APIエンドポイントのURL（ベースURL以外の部分）
 * @param {Options} [opts={}] - fetchオプション（ヘッダー、HTTPメソッドなど）
 *
 * @returns {Promise<T>} - APIレスポンスのデータ
 *
 * @example
 *
 * // GETリクエストを行う場合
 * const { data, error } = await useApiFetch<News[]>('/news');
 *
 * // POSTリクエストを行う場合
 * const { data, error } = await useApiFetch<Cart>('/carts', {
 *   method: 'POST',
 *   body: { productId: 1, quantity: 1 },
 * });
 *
 * 参考にした処理：
 * https://github.com/amrnn90/breeze-nuxt/blob/cc97bc3df9400079d1737872908bd8408b001bc8/utils/%24larafetch.ts#L20
 */
export const useApiFetch = async <T>(
  url: string,
  opts: Options = {}
): Promise<AsyncData<T, ErrorResponse>> => {
  const { endpoint, frontDomain } = useRuntimeConfig().public;

  const apiUrl = process.server
    ? useRuntimeConfig().public.ssrApiUrl
    : useRuntimeConfig().public.apiUrl;

  const requestId = `${process.server ? 'SERVER-' : 'CLIENT-'}${crypto.randomUUID()}`;
  const { headers: customHeaders = {}, method = 'GET', ...rest } = opts;

  // 基本ヘッダーの設定
  const headers: Record<string, string> = {
    RequestId: requestId,
    Accept: 'application/json',
    ...customHeaders,
  };

  // CSRFトークンが必要なメソッドの場合、トークンを取得してヘッダーに追加
  if (['post', 'delete', 'put', 'patch'].includes(method.toLowerCase())) {
    await initCsrf(apiUrl);
    const csrfToken = getCookie(CSRF_COOKIE) || '';
    headers[CSRF_HEADER] = csrfToken;
  }

  // キャッシュを無効にしたいのでkeyを設定
  const fetchOptions: FetchOptions = {
    baseURL: `${apiUrl}/${endpoint}`,
    headers,
    credentials: 'include',
    method,
    key: requestId,
    ...rest,
  };

  // サーバーサイドでの処理
  if (process.server) {
    const serverHeaders = useRequestHeaders(['cookie', 'referer']);
    fetchOptions.headers = {
      ...headers,
      cookie: serverHeaders['cookie'] || '',
      referer: serverHeaders['referer'] || frontDomain,
    };
  }

  const startTime = new Date();
  const result = (await useFetch<T, ErrorResponse>(url, fetchOptions)) as AsyncData<
    T,
    ErrorResponse
  >;

  // エラー処理
  if (result.error.value) {
    result.error.value = result.error.value.data;
  }

  return result;
};
